import Circle from '@arcgis/core/geometry/Circle'
import * as centroidOperator from '@arcgis/core/geometry/operators/centroidOperator'
import Point from '@arcgis/core/geometry/Point'
import Polygon from '@arcgis/core/geometry/Polygon'
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol'
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol'
import { Coordinate, DistanceUnit } from 'types/graphql'

import { MapShapeType } from 'src/components/shared/ArcGisMap/types'

export const getUnselectedSymbol = () =>
  new PictureMarkerSymbol({
    width: 15,
    height: 20,
    yoffset: 10,
    url: '/location-pin.svg',
  })

export const getSelectedSymbol = () =>
  new PictureMarkerSymbol({
    width: 15,
    height: 20,
    yoffset: 10,
    url: '/location-pin-selected.svg',
  })

export const getPolygonFillSymbol = () =>
  new SimpleFillSymbol({
    color: [0, 0, 0, 0.2],
    outline: {
      color: 'black',
      width: 1,
    },
  })

export const getHighlightedPolygonFillSymbol = () =>
  new SimpleFillSymbol({
    color: [37, 99, 235, 0.35],
    outline: {
      color: [37, 99, 235, 1],
      width: 2,
    },
  })

export const getTransparentFillSymbol = () =>
  new SimpleFillSymbol({
    color: [0, 0, 0, 0],
    outline: {
      color: [0, 0, 0, 0],
      width: 0,
    },
  })
export const getCentroidCoordinate = (mapShape: MapShapeType): Coordinate | null => {
  if (mapShape.type === 'coordinates') {
    if (mapShape.coordinates.length === 0) {
      return null
    }

    const coordinates = mapShape.coordinates
    if (coordinates.length > 1) {
      const polygon = new Polygon({
        rings: [
          coordinates.map((coordinate) => [coordinate.longitude ?? 0, coordinate.latitude ?? 0]),
        ],
      })
      return {
        longitude: polygon.centroid?.longitude ?? 0,
        latitude: polygon.centroid?.latitude ?? 0,
      }
    }

    const coordinate = coordinates[0]
    return {
      longitude: coordinate?.longitude ?? 0,
      latitude: coordinate?.latitude ?? 0,
    }
  }

  // Handle geometry case using centroid operator
  const geometry = mapShape.geometry
  const computedCentroid = centroidOperator.execute(geometry)
  if (computedCentroid?.type === 'point') {
    return {
      longitude: computedCentroid.longitude ?? 0,
      latitude: computedCentroid.latitude ?? 0,
    }
  }

  // Fallback to extent center
  if (geometry.extent?.center) {
    return {
      longitude: geometry.extent.center.longitude ?? 0,
      latitude: geometry.extent.center.latitude ?? 0,
    }
  }

  return null
}

export const getCentroidPoint = (mapShape: MapShapeType): Point | null => {
  const centroidCoordinates = getCentroidCoordinate(mapShape)
  if (!centroidCoordinates) {
    return null
  }

  return new Point({
    longitude: centroidCoordinates.longitude,
    latitude: centroidCoordinates.latitude,
  })
}

type CircleRadius = {
  radius: number
  radiusUnit: Circle['radiusUnit']
}

export const getCircleRadius = (
  distanceValue: number,
  distanceUnit: DistanceUnit
): CircleRadius => {
  switch (distanceUnit) {
    case 'Feet':
      return { radius: distanceValue, radiusUnit: 'feet' }
    case 'Miles':
      return { radius: distanceValue, radiusUnit: 'miles' }
    case 'Yards':
      return { radius: distanceValue, radiusUnit: 'yards' }
    case 'Centimeters': {
      const convertedDistanceValue = distanceValue * 0.01
      return { radius: convertedDistanceValue, radiusUnit: 'meters' }
    }
    case 'Inches': {
      const convertedDistanceValue = distanceValue * 0.0833333
      return { radius: convertedDistanceValue, radiusUnit: 'feet' }
    }
    case 'Kilometers':
      return { radius: distanceValue, radiusUnit: 'kilometers' }
    case 'Meters':
      return { radius: distanceValue, radiusUnit: 'meters' }
    case 'Millimeters': {
      const convertedDistanceValue = distanceValue * 0.001
      return { radius: convertedDistanceValue, radiusUnit: 'meters' }
    }
    default:
      throw new Error(`Unsupported distance unit: ${distanceUnit}`)
  }
}
